import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, FileText, Clock, CheckCircle, XCircle, Pencil, Send,
  Printer, PackageCheck, Ban, Loader2, Building2, Warehouse, Paperclip
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import api from '@/services/api';
import { formatDate, formatDateOnly } from '@/utils/dateFormat';

const statusConfig = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700', icon: FileText },
  pending_approval: { label: 'Pending Approval', color: 'bg-amber-100 text-amber-700', icon: Clock },
  approved: { label: 'Approved', color: 'bg-blue-100 text-blue-700', icon: CheckCircle },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700', icon: XCircle }
};

const money = (v, currency = 'INR') => {
  const symbol = currency === 'INR' ? '₹' : `${currency} `;
  return `${symbol}${(parseFloat(v) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const Field = ({ label, value, testId }) => (
  <div>
    <p className="text-xs text-gray-500">{label}</p>
    <p className="text-sm font-medium text-gray-800" data-testid={testId}>{value || '-'}</p>
  </div>
);

const PurchaseOrderViewPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [po, setPO] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchPO = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/po-v2/${id}`);
      setPO(res.data);
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to load purchase order',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [id, toast]);

  useEffect(() => { fetchPO(); }, [fetchPO]);

  const runAction = async (path, successMsg) => {
    setActionLoading(true);
    try {
      await api.put(`/po-v2/${id}/${path}`);
      toast({ title: 'Success', description: successMsg });
      fetchPO();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Action failed',
        variant: 'destructive'
      });
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-10 flex justify-center" data-testid="po-view-loading">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (!po) {
    return (
      <div className="p-10 text-center text-gray-500" data-testid="po-view-not-found">
        Purchase order not found.
        <div className="mt-4">
          <Button variant="outline" onClick={() => navigate('/hq/purchase/orders')}>Back to list</Button>
        </div>
      </div>
    );
  }

  const cfg = statusConfig[po.status] || statusConfig.draft;
  const StatusIcon = cfg.icon;
  const vendor = po.vendor_snapshot || {};
  const currency = po.currency || 'INR';

  return (
    <div className="p-6 lg:p-8 bg-gray-50 min-h-screen" data-testid="po-view-page">
      <Toaster />

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
        <div className="flex items-start gap-3">
          <Button variant="outline" size="icon" onClick={() => navigate('/hq/purchase/orders')} data-testid="po-view-back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-800" data-testid="po-view-number">{po.po_number}</h1>
              <Badge className={cfg.color} data-testid="po-view-status">
                <StatusIcon className="w-3 h-3 mr-1" />
                {cfg.label}
              </Badge>
            </div>
            <p className="text-gray-500 text-sm">
              Ordered {po.order_date ? formatDateOnly(po.order_date) : '-'}
              {po.expected_delivery_date ? ` · Expected ${formatDateOnly(po.expected_delivery_date)}` : ''}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => window.print()} data-testid="po-view-print-btn">
            <Printer className="w-4 h-4 mr-2" /> Print
          </Button>
          {po.status === 'draft' && (
            <>
              <Button variant="outline" onClick={() => navigate(`/hq/purchase/orders/${po.id}/edit`)} data-testid="po-view-edit-btn">
                <Pencil className="w-4 h-4 mr-2" /> Edit
              </Button>
              <Button disabled={actionLoading} onClick={() => runAction('submit-for-approval', 'PO submitted for approval')} data-testid="po-view-submit-btn">
                <Send className="w-4 h-4 mr-2" /> Submit for Approval
              </Button>
            </>
          )}
          {po.status === 'pending_approval' && (
            <Button disabled={actionLoading} onClick={() => runAction('approve', 'PO approved')} data-testid="po-view-approve-btn">
              <CheckCircle className="w-4 h-4 mr-2" /> Approve
            </Button>
          )}
          {po.status === 'approved' && (
            <Button onClick={() => navigate(`/hq/purchase/grn/local/new?po_id=${po.id}`)} data-testid="po-view-convert-grn-btn">
              <PackageCheck className="w-4 h-4 mr-2" /> Convert to GRN
            </Button>
          )}
          {['draft', 'pending_approval', 'approved'].includes(po.status) && (
            <Button variant="destructive" disabled={actionLoading} onClick={() => runAction('cancel', 'PO cancelled')} data-testid="po-view-cancel-btn">
              <Ban className="w-4 h-4 mr-2" /> Cancel
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Vendor */}
        <Card data-testid="po-view-vendor-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Building2 className="w-4 h-4 text-indigo-600" /> Vendor
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Field label="Name" value={vendor.vendor_name} testId="po-view-vendor-name" />
            <Field label="Code" value={vendor.vendor_code} />
            <Field label="Mobile" value={vendor.mobile} />
            <Field label="GSTIN" value={vendor.gstin} />
            <Field label="City / State" value={[vendor.city, vendor.state].filter(Boolean).join(', ')} />
          </CardContent>
        </Card>

        {/* Delivery */}
        <Card data-testid="po-view-delivery-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Warehouse className="w-4 h-4 text-indigo-600" /> Delivery
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Field label="Warehouse" value={po.warehouse_name} testId="po-view-warehouse" />
            <Field label="Currency" value={`${currency}${po.conversion_rate && currency !== 'INR' ? ` @ ${po.conversion_rate}` : ''}`} />
            <Field label="Order Date" value={po.order_date ? formatDateOnly(po.order_date) : '-'} />
            <Field label="Expected Delivery" value={po.expected_delivery_date ? formatDateOnly(po.expected_delivery_date) : '-'} />
            <Field label="GRN" value={po.grn_number} />
          </CardContent>
        </Card>

        {/* Totals */}
        <Card data-testid="po-view-totals-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-gray-500">Subtotal</span><span>{money(po.subtotal, currency)}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Bill Discount</span><span>- {money(po.bill_discount_amount, currency)}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">GST</span><span>{money(po.gst_total, currency)}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Other Charges (+)</span><span>{money(po.other_charges_add, currency)}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Other Charges (-)</span><span>- {money(po.other_charges_deduct, currency)}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Round Off</span><span>{money(po.round_off, currency)}</span></div>
            <div className="flex justify-between pt-2 border-t font-bold text-gray-800">
              <span>Grand Total</span>
              <span data-testid="po-view-grand-total">{money(po.grand_total, currency)}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Items */}
      <Card className="mt-6" data-testid="po-view-items-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Items ({(po.items || []).length})</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>#</TableHead>
                <TableHead>Product</TableHead>
                <TableHead>SKU</TableHead>
                <TableHead>HSN</TableHead>
                <TableHead className="text-right">Qty</TableHead>
                <TableHead className="text-right">Received</TableHead>
                <TableHead className="text-right">Rate</TableHead>
                <TableHead className="text-right">Disc %</TableHead>
                <TableHead className="text-right">GST %</TableHead>
                <TableHead className="text-right">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(po.items || []).map((item, i) => (
                <TableRow key={item.item_id || i} data-testid={`po-view-item-row-${i}`}>
                  <TableCell className="text-gray-400">{i + 1}</TableCell>
                  <TableCell className="font-medium text-gray-800">{item.product_name}</TableCell>
                  <TableCell className="text-gray-600">{item.sku}</TableCell>
                  <TableCell className="text-gray-600">{item.hsn_code || '-'}</TableCell>
                  <TableCell className="text-right">{item.qty} {item.uom}</TableCell>
                  <TableCell className="text-right">{item.received_qty || 0}</TableCell>
                  <TableCell className="text-right">{money(item.unit_price, currency)}</TableCell>
                  <TableCell className="text-right">{item.discount_percent || 0}%</TableCell>
                  <TableCell className="text-right">{item.gst_percent || 0}%</TableCell>
                  <TableCell className="text-right font-semibold">{money(item.total_amount, currency)}</TableCell>
                </TableRow>
              ))}
              {(po.items || []).length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} className="text-center text-gray-500 py-6">No items on this PO</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        {/* Status trail */}
        <Card data-testid="po-view-history-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Status Trail</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {(po.status_history || []).map((h, i) => (
              <div key={i} className="flex gap-3 text-sm" data-testid={`po-view-history-${i}`}>
                <div className="mt-1 w-2 h-2 rounded-full bg-indigo-500 shrink-0" />
                <div>
                  <p className="text-gray-800">
                    {statusConfig[h.to_status]?.label || h.to_status}
                    <span className="text-gray-400"> by {h.changed_by_name || 'system'}</span>
                  </p>
                  <p className="text-xs text-gray-500">{formatDate(h.changed_at)}{h.notes ? ` · ${h.notes}` : ''}</p>
                </div>
              </div>
            ))}
            {(po.status_history || []).length === 0 && (
              <p className="text-sm text-gray-500">No history recorded</p>
            )}
          </CardContent>
        </Card>

        {/* Notes / attachment */}
        <Card data-testid="po-view-notes-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Notes & Attachment</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{po.notes || 'No notes'}</p>
            {po.contract_url ? (
              <a
                href={po.contract_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 text-sm text-indigo-600 hover:underline"
                data-testid="po-view-contract-link"
              >
                <Paperclip className="w-4 h-4" /> View contract / quotation
              </a>
            ) : (
              <p className="text-xs text-gray-400">No contract attached</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PurchaseOrderViewPage;
